import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import http from "services/httpClient";
import type { ClientUserItem, ReportRecipient } from "./types";

type Props = {
  show: boolean;
  onHide: () => void;
  clientId: number;
  templateId: number | null;
  templateName?: string;
  onSaved?: () => void;
};

// 🔧 adjust if your backend differs
const CLIENT_USERS_ENDPOINT = (clientId: number) => `/clients/${clientId}/users`;

export function ReportRecipientsModal({
  show,
  onHide,
  clientId,
  templateId,
  templateName,
  onSaved,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [users, setUsers] = useState<ClientUserItem[]>([]);
  const [enabledByUserId, setEnabledByUserId] = useState<Record<number, boolean>>({});

  const load = async () => {
    if (!templateId) return;
    setLoading(true);
    setErr(null);

    try {
      const [usersRes, recRes] = await Promise.all([
        http.get(CLIENT_USERS_ENDPOINT(clientId)),
        http.get(`/reports/templates/${templateId}/recipients`),
      ]);

      const rawUsers = usersRes.data;

        const u: ClientUserItem[] = Array.isArray(rawUsers)
        ? rawUsers
        : Array.isArray(rawUsers?.items)
            ? rawUsers.items
            : Array.isArray(rawUsers?.users)
            ? rawUsers.users
            : [];
      const rec: ReportRecipient[] = recRes.data || [];

      setUsers(u);

      // build map from existing recipients
      const map: Record<number, boolean> = {};
      rec.forEach((r) => (map[r.user_id] = !!r.enabled));

      // default: if not present in map, set false (not subscribed yet)
      u.forEach((user) => {
        if (map[user.id] === undefined) map[user.id] = false;
      });

      setEnabledByUserId(map);
    } catch {
      setErr("Failed to load recipients. Check client users endpoint.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, templateId]);

  const toggle = (userId: number) => {
    setEnabledByUserId((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const selectAll = () => {
    setEnabledByUserId((prev) => {
      const next = { ...prev };
      users.forEach((u) => (next[u.id] = true));
      return next;
    });
  };

  const clearAll = () => {
    setEnabledByUserId((prev) => {
      const next = { ...prev };
      users.forEach((u) => (next[u.id] = false));
      return next;
    });
  };

  const save = async () => {
    if (!templateId) return;
    setSaving(true);
    setErr(null);

    try {
      const items = users.map((u) => ({
        user_id: u.id,
        enabled: !!enabledByUserId[u.id],
      }));

      await http.put(`/reports/templates/${templateId}/recipients`, { items });

      onHide();
      onSaved?.();
    } catch {
      setErr("Failed to save recipients.");
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = useMemo(() => {
    if (!Array.isArray(users)) return 0;
    return users.filter((u) => enabledByUserId[u.id]).length;
    }, [users, enabledByUserId]);

  const displayName = (u: ClientUserItem) => {
    const fn = (u.first_name || "").trim();
    const ln = (u.last_name || "").trim();
    const full = `${fn} ${ln}`.trim();
    return full || u.email;
  };

  return (
    <Modal
        show={show}
        onHide={onHide}
        centered
        size="lg"
        scrollable
        >
      <Modal.Header closeButton>
        <Modal.Title>
          Recipients{templateName ? ` — ${templateName}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
            maxHeight: "60vh",
            overflowY: "auto"
        }}
      >
        {err && <Alert variant="danger">{err}</Alert>}

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <div>Loading…</div>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="text-muted">
                Selected: <b>{selectedCount}</b>
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="outline-secondary" onClick={selectAll} disabled={!users.length}>
                  Select all
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={clearAll} disabled={!users.length}>
                  Clear
                </Button>
              </div>
            </div>

            <Table responsive hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 80 }}>Enabled</th>
                  {/* <th>Name</th> */}
                  <th>Email</th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={!!enabledByUserId[u.id]}
                        onChange={() => toggle(u.id)}
                      />
                    </td>
                    <td>{displayName(u)}</td>
                    {/* <td className="text-muted">{u.email}</td> */}
                  </tr>
                ))}

                {users.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      No users found for this client.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReportRecipientsModal;